import React from 'react';
import styled from 'styled-components';
import { useFormContext, Controller, ControllerRenderProps, ControllerFieldState, UseFormStateReturn } from 'react-hook-form';
import { Input, Toggle } from '../../common';
import PriceTypeSelector from '../../common/PriceTypeSelector';
import { PresetCreateRequest, LimitPriceType, PriceReference } from '../../../types/preset';

interface RiskManagementProps {
  // Keep any essential props not directly related to form fields, if any
}

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0 0 0.5rem 0;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const InfoText = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0;
`;

const WarningText = styled.p`
  color: #f59e0b;
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0;
  padding: 0.5rem;
  background: #fef3c7;
  border-radius: 4px;
  border-left: 3px solid #f59e0b;
`;

const PriceTypeSelectorGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1a1a1a;
`;

type ControllerRenderParams = {
  field: ControllerRenderProps<PresetCreateRequest, any>; 
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<PresetCreateRequest>; 
};

const RiskManagement: React.FC<RiskManagementProps> = () => {
  const { control, watch, formState: { errors }, setValue } = useFormContext<PresetCreateRequest>();

  const isTpEnabled = watch('take_profit.enabled');
  const isSlEnabled = watch('stop_loss.enabled');
  const tpPriceType = watch('take_profit.price_type');
  const slPriceType = watch('stop_loss.price_type');
  const tpPriceOffset = watch('take_profit.price_offset');
  const slPriceOffset = watch('stop_loss.price_offset');
  const tpPriceReference = watch('take_profit.price_reference');
  const slPriceReference = watch('stop_loss.price_reference');
  const reduceOnly = watch('flags.reduce_only');
  const orderType = watch('order_type');
  const useNativeTpSl = watch('use_native_tp_sl');

  // Show warning if take profit is enabled but reduce_only is false
  const showTpWarning = isTpEnabled && !reduceOnly;

  // More helpful warning message based on context
  const getTpWarningMessage = () => {
    if (orderType === 'market') {
      return "ℹ️ For market orders opening new positions, Take Profit will place a separate limit order to close the position at your target price. This is normal for position opening strategies.";
    } else {
      return "ℹ️ Take Profit will place a separate order to close your position. Consider enabling 'Reduce Only' in Order settings if you only want to close existing positions.";
    }
  };

  return (
    <StepContainer>
      {/* TP/SL Implementation Mode Toggle */}
      <FormSection>
        <SectionTitle>Take Profit / Stop Loss Mode</SectionTitle>
        <InputGroup>
          <Label>TP/SL Implementation</Label>
          <Controller
            name="use_native_tp_sl"
            control={control}
            defaultValue={true} // Default to native for MVP
            render={({ field }: ControllerRenderParams) => (
              <Toggle
                options={[
                  { value: 'false', label: 'Advanced TP/SL' }, 
                  { value: 'true', label: 'Simple TP/SL' }
                ]}
                value={field.value ? 'true' : 'false'}
                onChange={(value) => {
                  const useNative = value === 'true';
                  field.onChange(useNative);
                  
                  // Reset TP/SL settings when switching modes
                  if (useNative) {
                    // Clear advanced settings when switching to native
                    setValue('take_profit.price_type', LimitPriceType.ABSOLUTE);
                    setValue('take_profit.price_offset', null);
                    setValue('take_profit.price_reference', PriceReference.LAST);
                    setValue('stop_loss.price_type', LimitPriceType.ABSOLUTE);
                    setValue('stop_loss.price_offset', null);
                    setValue('stop_loss.price_reference', PriceReference.LAST);
                  }
                }}
              />
            )}
          />
          <InfoText>
            {useNativeTpSl 
              ? "🚀 Simple TP/SL: Uses HyperLiquid's built-in trigger system. Reliable, fast, and triggers on mark price. Perfect for most trading strategies."
              : "⚙️ Advanced TP/SL: Sophisticated price calculations with multiple reference points. For power users who need complex trigger logic."
            }
          </InfoText>
        </InputGroup>
      </FormSection>

      {/* Native TP/SL Section */}
      {useNativeTpSl && (
        <>
          <FormSection>
            <SectionTitle>Simple Take Profit</SectionTitle>
            <InputGroup>
              <Label>Enable Take Profit</Label>
              <Controller
                name="take_profit.enabled"
                control={control}
                defaultValue={false}
                render={({ field }: ControllerRenderParams) => (
                  <Toggle
                    options={[{ value: 'false', label: 'Off' }, { value: 'true', label: 'On' }]}
                    value={field.value ? 'true' : 'false'}
                    onChange={(value) => {
                      const enabled = value === 'true';
                      field.onChange(enabled);
                      if (!enabled) {
                        setValue('take_profit.trigger_price_value', null);
                      }
                    }}
                  />
                )}
              />
            </InputGroup>
            
            {isTpEnabled && (
              <InputGroup>
                <Label>Take Profit Price</Label>
                <Controller
                  name="take_profit.trigger_price_value"
                  control={control}
                  rules={{ required: isTpEnabled ? 'Take profit price is required' : false }}
                  render={({ field }: ControllerRenderParams) => (
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Enter take profit trigger price"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const numValue = e.target.value ? parseFloat(e.target.value) : null;
                        field.onChange(numValue);
                      }}
                    />
                  )}
                />
                {errors.take_profit?.trigger_price_value && <ErrorText>{errors.take_profit.trigger_price_value.message}</ErrorText>}
                <InfoText>
                  Exact price to trigger take profit. Uses HyperLiquid's native trigger system (triggers on mark price).
                </InfoText>
              </InputGroup>
            )}
          </FormSection>

          <FormSection>
            <SectionTitle>Simple Stop Loss</SectionTitle>
            <InputGroup>
              <Label>Enable Stop Loss</Label>
              <Controller
                name="stop_loss.enabled"
                control={control}
                defaultValue={false}
                render={({ field }: ControllerRenderParams) => (
                  <Toggle
                    options={[{ value: 'false', label: 'Off' }, { value: 'true', label: 'On' }]}
                    value={field.value ? 'true' : 'false'}
                    onChange={(value) => {
                      const enabled = value === 'true';
                      field.onChange(enabled);
                      if (!enabled) {
                        setValue('stop_loss.trigger_price_value', null);
                      }
                    }}
                  />
                )}
              />
            </InputGroup>
            
            {isSlEnabled && (
              <InputGroup>
                <Label>Stop Loss Price</Label>
                <Controller
                  name="stop_loss.trigger_price_value"
                  control={control}
                  rules={{ required: isSlEnabled ? 'Stop loss price is required' : false }}
                  render={({ field }: ControllerRenderParams) => (
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Enter stop loss trigger price"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const numValue = e.target.value ? parseFloat(e.target.value) : null;
                        field.onChange(numValue);
                      }}
                    />
                  )}
                />
                {errors.stop_loss?.trigger_price_value && <ErrorText>{errors.stop_loss.trigger_price_value.message}</ErrorText>}
                <InfoText>
                  Exact price to trigger stop loss. Uses HyperLiquid's native trigger system (triggers on mark price).
                </InfoText>
              </InputGroup>
            )}
          </FormSection>
        </>
      )}

      {/* Advanced TP/SL Section (Original Implementation) */}
      {!useNativeTpSl && (
        <>
          <FormSection>
            <SectionTitle>Advanced Take Profit Settings</SectionTitle>
            <InputGroup>
              <Label>Enable Take Profit</Label>
              <Controller
                name="take_profit.enabled"
                control={control}
                defaultValue={false}
                render={({ field }: ControllerRenderParams) => (
                  <Toggle
                    options={[{ value: 'false', label: 'Off' }, { value: 'true', label: 'On' }]}
                    value={field.value ? 'true' : 'false'}
                    onChange={(value) => {
                      const enabled = value === 'true';
                      field.onChange(enabled);
                      if (!enabled) {
                        setValue('take_profit.price_type', LimitPriceType.ABSOLUTE);
                        setValue('take_profit.trigger_price_value', null);
                        setValue('take_profit.price_offset', null);
                        setValue('take_profit.price_reference', PriceReference.LAST);
                      }
                    }}
                  />
                )}
              />
            </InputGroup>
            
            {showTpWarning && (
              <WarningText>
                {getTpWarningMessage()}
              </WarningText>
            )}
            
            {isTpEnabled && (
              <PriceTypeSelectorGroup>
                <Controller
                  name="take_profit.price_type"
                  control={control}
                  defaultValue={LimitPriceType.ABSOLUTE}
                  render={({ field }: ControllerRenderParams) => (
                    <PriceTypeSelector
                      priceType={field.value || LimitPriceType.ABSOLUTE}
                      onPriceTypeChange={(type) => {
                        field.onChange(type);
                        // Reset related fields when price type changes
                        setValue('take_profit.price_offset', null);
                        setValue('take_profit.price_reference', PriceReference.LAST);
                      }}
                      priceOffset={tpPriceOffset}
                      onPriceOffsetChange={(offset) => setValue('take_profit.price_offset', offset)}
                      priceReference={tpPriceReference || PriceReference.LAST}
                      onPriceReferenceChange={(reference) => setValue('take_profit.price_reference', reference)}
                      label="Take Profit Price Type"
                      tooltipContent="Configure how the take profit trigger price is calculated relative to your entry price or market references"
                    />
                  )}
                />
                {errors.take_profit?.price_type && <ErrorText>{errors.take_profit.price_type.message}</ErrorText>}

                <InputGroup>
                  <Label>
                    {tpPriceType === LimitPriceType.ABSOLUTE ? 'Trigger Price' : 'Trigger Price Value'}
                  </Label>
                  <Controller
                    name="take_profit.trigger_price_value"
                    control={control}
                    rules={{ required: 'Take profit trigger price is required' }}
                    render={({ field }: ControllerRenderParams) => (
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={
                          tpPriceType === LimitPriceType.ABSOLUTE ? 'Enter exact trigger price' :
                          tpPriceType === LimitPriceType.BEST_BID || tpPriceType === LimitPriceType.BEST_ASK ? 'Enter base trigger price' :
                          tpPriceType === LimitPriceType.RELATIVE ? 'Enter base price for offset calculation' :
                          tpPriceType === LimitPriceType.PERCENT ? 'Enter base price for percentage calculation' :
                          'Enter trigger price value'
                        }
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const numValue = e.target.value ? parseFloat(e.target.value) : null;
                          field.onChange(numValue);
                        }}
                      />
                    )}
                  />
                  {errors.take_profit?.trigger_price_value && <ErrorText>{errors.take_profit.trigger_price_value.message}</ErrorText>}
                  <InfoText>
                    {tpPriceType === LimitPriceType.ABSOLUTE ? 'Exact price at which take profit will trigger' :
                     tpPriceType === LimitPriceType.BEST_BID ? 'Base price + offset from best bid' :
                     tpPriceType === LimitPriceType.BEST_ASK ? 'Base price + offset from best ask' :
                     tpPriceType === LimitPriceType.RELATIVE ? 'Base price + offset from selected reference' :
                     tpPriceType === LimitPriceType.PERCENT ? 'Base price + percentage of selected reference' :
                     'Calculated trigger price based on selected type and parameters'}
                  </InfoText>
                </InputGroup>
              </PriceTypeSelectorGroup>
            )}
          </FormSection>
          
          <FormSection>
            <SectionTitle>Stop Loss Settings</SectionTitle>
            <InputGroup>
              <Label>Enable Stop Loss</Label>
              <Controller
                name="stop_loss.enabled"
                control={control}
                defaultValue={false}
                render={({ field }: ControllerRenderParams) => (
                  <Toggle
                    options={[{ value: 'false', label: 'Off' }, { value: 'true', label: 'On' }]}
                    value={field.value ? 'true' : 'false'}
                    onChange={(value) => {
                      const enabled = value === 'true';
                      field.onChange(enabled);
                      if (!enabled) {
                        setValue('stop_loss.price_type', LimitPriceType.ABSOLUTE);
                        setValue('stop_loss.trigger_price_value', null);
                        setValue('stop_loss.price_offset', null);
                        setValue('stop_loss.price_reference', PriceReference.LAST);
                      }
                    }}
                  />
                )}
              />
            </InputGroup>
            
            {isSlEnabled && (
              <PriceTypeSelectorGroup>
                <Controller
                  name="stop_loss.price_type"
                  control={control}
                  defaultValue={LimitPriceType.ABSOLUTE}
                  render={({ field }: ControllerRenderParams) => (
                    <PriceTypeSelector
                      priceType={field.value || LimitPriceType.ABSOLUTE}
                      onPriceTypeChange={(type) => {
                        field.onChange(type);
                        // Reset related fields when price type changes
                        setValue('stop_loss.price_offset', null);
                        setValue('stop_loss.price_reference', PriceReference.LAST);
                      }}
                      priceOffset={slPriceOffset}
                      onPriceOffsetChange={(offset) => setValue('stop_loss.price_offset', offset)}
                      priceReference={slPriceReference || PriceReference.LAST}
                      onPriceReferenceChange={(reference) => setValue('stop_loss.price_reference', reference)}
                      label="Stop Loss Price Type"
                      tooltipContent="Configure how the stop loss trigger price is calculated relative to your entry price or market references"
                    />
                  )}
                />
                {errors.stop_loss?.price_type && <ErrorText>{errors.stop_loss.price_type.message}</ErrorText>}

                <InputGroup>
                  <Label>
                    {slPriceType === LimitPriceType.ABSOLUTE ? 'Trigger Price' : 'Trigger Price Value'}
                  </Label>
                  <Controller
                    name="stop_loss.trigger_price_value"
                    control={control}
                    rules={{ required: 'Stop loss trigger price is required' }}
                    render={({ field }: ControllerRenderParams) => (
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={
                          slPriceType === LimitPriceType.ABSOLUTE ? 'Enter exact trigger price' :
                          slPriceType === LimitPriceType.BEST_BID || slPriceType === LimitPriceType.BEST_ASK ? 'Enter base trigger price' :
                          slPriceType === LimitPriceType.RELATIVE ? 'Enter base price for offset calculation' :
                          slPriceType === LimitPriceType.PERCENT ? 'Enter base price for percentage calculation' :
                          'Enter trigger price value'
                        }
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const numValue = e.target.value ? parseFloat(e.target.value) : null;
                          field.onChange(numValue);
                        }}
                      />
                    )}
                  />
                  {errors.stop_loss?.trigger_price_value && <ErrorText>{errors.stop_loss.trigger_price_value.message}</ErrorText>}
                  <InfoText>
                    {slPriceType === LimitPriceType.ABSOLUTE ? 'Exact price at which stop loss will trigger' :
                     slPriceType === LimitPriceType.BEST_BID ? 'Base price + offset from best bid' :
                     slPriceType === LimitPriceType.BEST_ASK ? 'Base price + offset from best ask' :
                     slPriceType === LimitPriceType.RELATIVE ? 'Base price + offset from selected reference' :
                     slPriceType === LimitPriceType.PERCENT ? 'Base price + percentage of selected reference' :
                     'Calculated trigger price based on selected type and parameters'}
                  </InfoText>
                </InputGroup>
              </PriceTypeSelectorGroup>
            )}
          </FormSection>
        </>
      )}
    </StepContainer>
  );
};

export default RiskManagement; 